import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { X, Search, User, UserPlus, Check } from "lucide-react";
import { STATUS_OPTIONS, STATUS_LABELS, TIME_SLOTS } from "./shared";
import VeniceSubPicker, { VENICE_LABEL, VENICE_NEAR, venicedbKey, veniceDisplayLabel } from "../VeniceSubPicker";
import SpecificAddressField from "../SpecificAddressField";
import { extractApiError } from "../../lib/apiError";

const PRIORITY_LOCATIONS = [VENICE_LABEL];

export default function AddBookingModal({ routes, onClose, onSuccess }) {
  const [form, setForm] = useState({
    origin: "", destination: "", route_id: "",
    origin_specific_address: "", destination_specific_address: "",
    pickup_date: null, pickup_time: "10:00",
    return_date: null, return_time: "10:00",
    passengers: 2, luggage: 1, is_luxury: false, is_round_trip: false,
    luxury_option_ids: [],
    user_name: "", user_email: "", user_phone: "", notes: "", status: "confirmed",
    custom_route: false, base_price_override: "",
    vehicle_id: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [luxuryOpts, setLuxuryOpts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/customers`)
      .then(res => setCustomers(res.data || []))
      .catch(() => { /* non-blocking */ });
    axios.get(`${API}/admin/luxury-options`)
      .then(res => setLuxuryOpts(res.data || []))
      .catch(() => { /* non-blocking */ });
    axios.get(`${API}/admin/vehicles`)
      .then(res => setVehicles(res.data || []))
      .catch(() => { /* non-blocking */ });
  }, []);

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    return customers.filter(c =>
      c.email?.toLowerCase().includes(q) ||
      c.name?.toLowerCase().includes(q) ||
      c.phone?.includes(q)
    ).slice(0, 8);
  }, [customers, customerSearch]);

  const selectCustomer = (c) => {
    setForm(p => ({
      ...p,
      user_name: c.name || "",
      user_email: c.email || "",
      user_phone: c.phone || ""
    }));
    setSelectedCustomer(c);
    setCustomerSearch(`${c.name || c.email}`);
    setShowSuggestions(false);
  };

  const clearSelection = () => {
    setSelectedCustomer(null);
    setCustomerSearch("");
    setForm(p => ({ ...p, user_name: "", user_email: "", user_phone: "" }));
  };

  const origins = useMemo(() => {
    const set = new Set();
    routes.forEach(r => {
      set.add(veniceDisplayLabel(r.origin));
      set.add(veniceDisplayLabel(r.destination));
    });
    const all = Array.from(set);
    const priority = PRIORITY_LOCATIONS.filter(p => all.includes(p));
    const rest = all.filter(l => !PRIORITY_LOCATIONS.includes(l)).sort();
    return { priority, rest };
  }, [routes]);

  const destinations = useMemo(() => {
    if (!form.origin) return { priority: [], rest: [] };
    const originKey = venicedbKey(form.origin);
    const map = new Map();
    routes.forEach(r => {
      if (r.origin === originKey) {
        const dispDest = veniceDisplayLabel(r.destination);
        if (!map.has(dispDest)) map.set(dispDest, { destination: dispDest, routeId: r.route_id });
      } else if (r.destination === originKey) {
        const dispDest = veniceDisplayLabel(r.origin);
        if (!map.has(dispDest)) map.set(dispDest, { destination: dispDest, routeId: r.route_id });
      }
    });
    const all = Array.from(map.values());
    const priority = PRIORITY_LOCATIONS.map(p => all.find(d => d.destination === p)).filter(Boolean);
    const rest = all.filter(d => !PRIORITY_LOCATIONS.includes(d.destination)).sort((a, b) => a.destination.localeCompare(b.destination));
    return { priority, rest };
  }, [routes, form.origin]);

  const resolveRouteId = (origin, destination) => {
    if (!origin || !destination || origin === VENICE_LABEL || destination === VENICE_LABEL) return "";
    const o = venicedbKey(origin);
    const d = venicedbKey(destination);
    const route = routes.find(r =>
      (r.origin === o && r.destination === d) ||
      (r.origin === d && r.destination === o)
    );
    return route?.route_id || "";
  };

  const handleOriginChange = (v) => setForm(p => ({ ...p, origin: v, destination: "", route_id: "", origin_specific_address: "", destination_specific_address: "" }));
  const handleDestinationChange = (v) => {
    setForm(p => ({ ...p, destination: v, destination_specific_address: "", route_id: resolveRouteId(p.origin, v) }));
  };
  const handleVeniceOriginPick = (full) => setForm(p => ({ ...p, origin: full, origin_specific_address: full === VENICE_NEAR ? p.origin_specific_address : "", route_id: resolveRouteId(full, p.destination) }));
  const handleVeniceDestinationPick = (full) => setForm(p => ({ ...p, destination: full, destination_specific_address: full === VENICE_NEAR ? p.destination_specific_address : "", route_id: resolveRouteId(p.origin, full) }));

  const handleSwap = () => {
    if (!form.origin || !form.destination) return;
    setForm(p => ({
      ...p,
      origin: p.destination,
      destination: p.origin,
      origin_specific_address: p.destination_specific_address,
      destination_specific_address: p.origin_specific_address,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.origin || !form.destination || !form.pickup_date || !form.user_name || !form.user_email || !form.user_phone) {
      toast.error("Please fill all required fields"); return;
    }
    if (!form.custom_route && (form.origin === VENICE_LABEL || form.destination === VENICE_LABEL)) {
      toast.error("Please choose Piazzale Roma, Airport or Near Venice for Venice"); return;
    }
    // Venice → Venice: cannot be same variant on both sides
    if (!form.custom_route && form.origin && form.destination && form.origin === form.destination &&
        [VENICE_NEAR, "Venice - Piazzale Roma", "Venice Airport"].includes(form.origin)) {
      toast.error("Pickup and drop-off cannot be the same Venice point"); return;
    }
    if (form.custom_route && (!form.base_price_override || parseFloat(form.base_price_override) <= 0)) {
      toast.error("Please enter a custom price for the custom route"); return;
    }
    const oAddr = (form.origin_specific_address || "").trim();
    const dAddr = (form.destination_specific_address || "").trim();
    if (form.origin === VENICE_NEAR && !oAddr) {
      toast.error("Please enter the pickup address near Venice (within 15 km)"); return;
    }
    if (form.destination === VENICE_NEAR && !dAddr) {
      toast.error("Please enter the drop-off address near Venice (within 15 km)"); return;
    }
    if (form.origin_specific_address && !oAddr) { toast.error("Pickup specific address cannot be empty"); return; }
    if (form.destination_specific_address && !dAddr) { toast.error("Drop-off specific address cannot be empty"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        origin_specific_address: oAddr || null,
        destination_specific_address: dAddr || null,
        pickup_date: format(form.pickup_date, "yyyy-MM-dd"),
        return_date: form.is_round_trip && form.return_date ? format(form.return_date, "yyyy-MM-dd") : null,
        return_time: form.is_round_trip ? form.return_time : null,
        base_price_override: form.custom_route ? parseFloat(form.base_price_override) : null,
      };
      delete payload.custom_route;
      if (!payload.base_price_override) delete payload.base_price_override;
      // For custom routes, omit route_id so backend treats origin/destination as free-form
      if (form.custom_route) payload.route_id = null;
      await axios.post(`${API}/admin/bookings`, payload);
      toast.success("Booking created!");
      onSuccess();
    } catch (error) { toast.error(extractApiError(error, "Failed to create booking")); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="add-booking-modal">
      <div className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <h3 className="text-lg font-semibold text-white">Add Booking Manually</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <label className="flex items-center gap-2 text-white/60 text-xs cursor-pointer select-none bg-[#1A1A1A] border border-white/5 rounded-sm px-3 py-2" data-testid="custom-route-toggle">
            <Switch
              checked={form.custom_route}
              onCheckedChange={(c) => setForm(p => ({ ...p, custom_route: c, route_id: c ? "" : p.route_id }))}
              data-testid="custom-route-switch"
            />
            <span>Custom origin / destination &amp; price (free-form, off-catalog)</span>
          </label>

          {form.custom_route ? (
            <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-end">
              <div>
                <label className="text-white/50 text-xs mb-1 block">From *</label>
                <Input
                  value={form.origin}
                  onChange={(e) => setForm(p => ({ ...p, origin: e.target.value }))}
                  className="input-dark"
                  placeholder="e.g. Treviso"
                  data-testid="modal-origin-custom"
                />
              </div>
              <button type="button" onClick={handleSwap} disabled={!form.origin || !form.destination}
                className="h-10 w-10 mb-0.5 bg-[#1F1F1F] border border-[#333] hover:border-[#C5A059]/40 disabled:opacity-30 text-[#C5A059] flex items-center justify-center rounded-sm transition-colors"
                title="Swap From/To">
                &#8644;
              </button>
              <div>
                <label className="text-white/50 text-xs mb-1 block">To *</label>
                <Input
                  value={form.destination}
                  onChange={(e) => setForm(p => ({ ...p, destination: e.target.value }))}
                  className="input-dark"
                  placeholder="e.g. Custom Hotel Lake Garda"
                  data-testid="modal-destination-custom"
                />
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-start">
            <div>
              <label className="text-white/50 text-xs mb-1 block">From *</label>
              <Select value={veniceDisplayLabel(form.origin)} onValueChange={handleOriginChange}>
                <SelectTrigger className="input-dark w-full" data-testid="modal-origin"><SelectValue placeholder="Origin" /></SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-72">
                  {origins.priority.map(o => <SelectItem key={o} value={o} className="text-white hover:bg-[#333]">{o}</SelectItem>)}
                  {origins.priority.length > 0 && origins.rest.length > 0 && <SelectSeparator className="my-1 bg-[#C5A059]/30" />}
                  {origins.rest.map(o => <SelectItem key={o} value={o} className="text-white hover:bg-[#333]">{o}</SelectItem>)}
                </SelectContent>
              </Select>
              <VeniceSubPicker
                value={form.origin}
                onSelect={handleVeniceOriginPick}
                testIdPrefix="modal-origin"
                label="Pickup point"
                nearAddress={form.origin_specific_address}
                onChangeNearAddress={(v) => setForm(p => ({ ...p, origin_specific_address: v }))}
                disabledVariant={[VENICE_NEAR, "Venice - Piazzale Roma", "Venice Airport"].includes(form.destination) ? form.destination : null}
              />
              <SpecificAddressField
                location={form.origin}
                value={form.origin_specific_address}
                onChange={(v) => setForm(p => ({ ...p, origin_specific_address: v }))}
                testIdPrefix="modal-origin"
                sideLabel="the pickup location"
              />
            </div>
            <button type="button" onClick={handleSwap} disabled={!form.origin || !form.destination}
              className="h-10 w-10 mt-6 bg-[#1F1F1F] border border-[#333] hover:border-[#C5A059]/40 disabled:opacity-30 text-[#C5A059] flex items-center justify-center rounded-sm transition-colors"
              title="Swap From/To" data-testid="modal-swap">
              &#8644;
            </button>
            <div>
              <label className="text-white/50 text-xs mb-1 block">To *</label>
              <Select value={veniceDisplayLabel(form.destination)} onValueChange={handleDestinationChange} disabled={!form.origin || form.origin === VENICE_LABEL}>
                <SelectTrigger className="input-dark w-full" data-testid="modal-destination"><SelectValue placeholder="Destination" /></SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-72">
                  {destinations.priority.map(d => <SelectItem key={d.routeId} value={d.destination} className="text-white hover:bg-[#333]">{d.destination}</SelectItem>)}
                  {destinations.priority.length > 0 && destinations.rest.length > 0 && <SelectSeparator className="my-1 bg-[#C5A059]/30" />}
                  {destinations.rest.map(d => <SelectItem key={d.routeId} value={d.destination} className="text-white hover:bg-[#333]">{d.destination}</SelectItem>)}
                </SelectContent>
              </Select>
              <VeniceSubPicker
                value={form.destination}
                onSelect={handleVeniceDestinationPick}
                testIdPrefix="modal-destination"
                label="Drop-off point"
                nearAddress={form.destination_specific_address}
                onChangeNearAddress={(v) => setForm(p => ({ ...p, destination_specific_address: v }))}
                disabledVariant={[VENICE_NEAR, "Venice - Piazzale Roma", "Venice Airport"].includes(form.origin) ? form.origin : null}
              />
              <SpecificAddressField
                location={form.destination}
                value={form.destination_specific_address}
                onChange={(v) => setForm(p => ({ ...p, destination_specific_address: v }))}
                testIdPrefix="modal-destination"
                sideLabel="the drop-off location"
              />
            </div>
          </div>
          )}

          {form.custom_route && (
            <div>
              <label className="text-white/50 text-xs mb-1 block">Custom price (\u20ac) *</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.base_price_override}
                onChange={(e) => setForm(p => ({ ...p, base_price_override: e.target.value }))}
                className="input-dark"
                placeholder="Base fare before night/luxury surcharges"
                data-testid="modal-custom-price"
                required
              />
              <p className="text-white/30 text-[10px] mt-1">Night and luxury surcharges are still applied automatically.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-xs mb-1 block">Pickup Date *</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="input-dark w-full justify-start text-left font-normal" data-testid="modal-date">
                    {form.pickup_date ? format(form.pickup_date, "dd MMM yyyy") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#1F1F1F] border-[#333]" align="start">
                  <Calendar mode="single" selected={form.pickup_date} onSelect={(d) => setForm(p => ({ ...p, pickup_date: d }))} className="text-white" />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="text-white/50 text-xs mb-1 block">Time *</label>
              <Select value={form.pickup_time} onValueChange={(v) => setForm(p => ({ ...p, pickup_time: v }))}>
                <SelectTrigger className="input-dark w-full"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-60">{TIME_SLOTS.map(t => <SelectItem key={t} value={t} className="text-white hover:bg-[#333]">{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="text-white/50 text-xs mb-1 block">Passengers</label>
              <Select value={form.passengers.toString()} onValueChange={(v) => setForm(p => ({ ...p, passengers: parseInt(v) }))}>
                <SelectTrigger className="input-dark w-full" data-testid="modal-passengers-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333]">{Array.from({ length: Math.max(1, Math.max(...vehicles.map(v => v.max_passengers || 0), 12)) }, (_, i) => i + 1).map(n => <SelectItem key={n} value={n.toString()} className="text-white hover:bg-[#333]">{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs mb-1 block">Luggage</label>
              <Select value={(form.luggage ?? 1).toString()} onValueChange={(v) => setForm(p => ({ ...p, luggage: parseInt(v) }))}>
                <SelectTrigger className="input-dark w-full" data-testid="modal-luggage-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1F1F1F] border-[#333]">{Array.from({ length: Math.max(1, Math.max(...vehicles.map(v => v.max_luggage || 0), 12) + 1) }, (_, i) => i).map(n => <SelectItem key={n} value={n.toString()} className="text-white hover:bg-[#333]">{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2 pb-1"><Switch checked={form.is_luxury} onCheckedChange={(c) => setForm(p => ({ ...p, is_luxury: c }))} /><span className="text-white/50 text-xs">Luxury</span></div>
            <div className="flex items-end gap-2 pb-1"><Switch checked={form.is_round_trip} onCheckedChange={(c) => setForm(p => ({ ...p, is_round_trip: c }))} /><span className="text-white/50 text-xs">Round Trip</span></div>
          </div>
          {form.is_round_trip && (
            <div className="grid grid-cols-2 gap-4 p-3 bg-[#C5A059]/5 border border-[#C5A059]/20 rounded-sm">
              <div>
                <label className="text-white/50 text-xs mb-1 block">Return Date</label>
                <Popover>
                  <PopoverTrigger asChild><Button variant="outline" className="input-dark w-full justify-start text-left font-normal">{form.return_date ? format(form.return_date, "dd MMM yyyy") : "Select"}</Button></PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#1F1F1F] border-[#333]" align="start">
                    <Calendar mode="single" selected={form.return_date} onSelect={(d) => setForm(p => ({ ...p, return_date: d }))} disabled={(d) => d < (form.pickup_date || new Date())} className="text-white" />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-white/50 text-xs mb-1 block">Return Time</label>
                <Select value={form.return_time} onValueChange={(v) => setForm(p => ({ ...p, return_time: v }))}>
                  <SelectTrigger className="input-dark w-full"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-60">{TIME_SLOTS.map(t => <SelectItem key={t} value={t} className="text-white hover:bg-[#333]">{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="border-t border-white/5 pt-4">
            <p className="text-white/50 text-xs uppercase tracking-wide mb-3">Customer Details</p>

            <div className="relative mb-4">
              <label className="text-white/50 text-xs mb-1 block flex items-center gap-1">
                <Search className="w-3 h-3" /> Find existing customer (optional)
              </label>
              <div className="relative">
                <Input
                  value={customerSearch}
                  onChange={(e) => { setCustomerSearch(e.target.value); setShowSuggestions(true); setSelectedCustomer(null); }}
                  onFocus={() => setShowSuggestions(true)}
                  placeholder="Type name, email, or phone (min 2 chars)..."
                  className="input-dark pr-9"
                  data-testid="customer-search-input"
                />
                {selectedCustomer ? (
                  <button type="button" onClick={clearSelection} className="absolute right-2 top-1/2 -translate-y-1/2 text-emerald-400 hover:text-red-400" title="Clear">
                    <Check className="w-4 h-4" />
                  </button>
                ) : customerSearch ? (
                  <button type="button" onClick={() => { setCustomerSearch(""); setShowSuggestions(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 hover:text-white">
                    <X className="w-4 h-4" />
                  </button>
                ) : null}
              </div>

              {showSuggestions && filteredCustomers.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-[#1A1A1A] border border-[#333] rounded-sm shadow-xl max-h-72 overflow-y-auto" data-testid="customer-suggestions">
                  {filteredCustomers.map(c => (
                    <button
                      key={c.email}
                      type="button"
                      onClick={() => selectCustomer(c)}
                      className="w-full text-left px-3 py-2 hover:bg-[#C5A059]/10 border-b border-white/5 last:border-b-0 flex items-center gap-3"
                      data-testid={`customer-suggestion-${c.email}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-[#C5A059]/20 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-[#C5A059]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{c.name || "(no name)"}</p>
                        <p className="text-white/40 text-xs truncate">{c.email}{c.phone && ` • ${c.phone}`}</p>
                      </div>
                      {c.total_orders > 0 && (
                        <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded whitespace-nowrap">
                          {c.total_orders} {c.total_orders === 1 ? "booking" : "bookings"}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {showSuggestions && customerSearch.trim().length >= 2 && filteredCustomers.length === 0 && (
                <div className="absolute z-10 mt-1 w-full bg-[#1A1A1A] border border-[#333] rounded-sm p-3 text-white/40 text-xs flex items-center gap-2">
                  <UserPlus className="w-3 h-3" /> No match. Fill the fields below to add a new customer.
                </div>
              )}
            </div>

            {selectedCustomer && (
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-2 mb-3 flex items-center gap-2 text-xs">
                <Check className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-emerald-300">Auto-filled from <strong>{selectedCustomer.email}</strong> ({selectedCustomer.total_orders || 0} previous bookings)</span>
              </div>
            )}

            <div className="space-y-3">
              <Input placeholder="Full Name *" value={form.user_name} onChange={(e) => setForm(p => ({ ...p, user_name: e.target.value }))} className="input-dark" data-testid="modal-name" />
              <Input type="email" placeholder="Email *" value={form.user_email} onChange={(e) => setForm(p => ({ ...p, user_email: e.target.value }))} className="input-dark" data-testid="modal-email" />
              <Input type="tel" placeholder="Phone *" value={form.user_phone} onChange={(e) => setForm(p => ({ ...p, user_phone: e.target.value }))} className="input-dark" data-testid="modal-phone" />
              <Input placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} className="input-dark" />
            </div>
          </div>
          {/* Vehicle (optional manual assignment) */}
          {vehicles.length > 0 && (
            <div data-testid="modal-vehicle-section">
              <label className="text-white/50 text-xs mb-1 block">Vehicle (optional)</label>
              <select
                value={form.vehicle_id || ""}
                onChange={(e) => setForm(p => ({ ...p, vehicle_id: e.target.value || null }))}
                className="input-dark text-sm w-full"
                data-testid="modal-vehicle-select"
              >
                <option value="">— Auto-assign (recommended) —</option>
                {vehicles.filter(v => v.published !== false).map(v => (
                  <option key={v.vehicle_id} value={v.vehicle_id}>
                    {v.title} ({v.max_passengers} pax, {v.max_luggage} bags)
                  </option>
                ))}
              </select>
              <p className="text-white/30 text-[10px] mt-1">Leave on "Auto-assign" to let the system pick based on passenger/luggage caps or Premium options.</p>
            </div>
          )}

          {/* Premium Enhancements (multi-select) — hides options whose linked
              vehicle can't seat the chosen pax. */}
          {luxuryOpts.length > 0 && (() => {
            const visible = luxuryOpts.filter((opt) => {
              const vid = opt.auto_assign_vehicle_id;
              if (!vid) return true;
              const veh = vehicles.find(v => v.vehicle_id === vid);
              if (!veh) return true;
              return (form.passengers || 1) <= (veh.max_passengers || 0);
            });
            if (visible.length === 0) return null;
            return (
              <div className="bg-[#1F1F1F]/40 rounded-sm p-3 space-y-2" data-testid="modal-premium-section">
                <p className="text-white/60 text-xs uppercase tracking-wide">Premium enhancements (optional)</p>
                {visible.map((opt) => {
                  const checked = (form.luxury_option_ids || []).includes(opt.luxury_option_id);
                  return (
                    <label
                      key={opt.luxury_option_id}
                      className={`flex items-start gap-2 p-2 rounded-sm cursor-pointer text-xs ${checked ? "bg-[#C5A059]/10 border border-[#C5A059]/30" : "bg-[#0F0F0F] border border-white/5"}`}
                      data-testid={`modal-premium-${opt.luxury_option_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const set = new Set(form.luxury_option_ids || []);
                          if (e.target.checked) set.add(opt.luxury_option_id); else set.delete(opt.luxury_option_id);
                          setForm(p => ({ ...p, luxury_option_ids: Array.from(set) }));
                        }}
                        className="mt-0.5 accent-[#C5A059]"
                      />
                      <span className="flex-1">
                        <span className="text-white font-medium">{opt.title}</span>
                        <span className="ml-2 text-[#C5A059] text-[10px]">+{opt.surcharge_pct}%</span>
                        {opt.auto_assign_vehicle_id && (
                          <span className="ml-2 text-[10px] bg-purple-500/15 text-purple-300 px-1.5 py-0.5 rounded">auto-assigns vehicle</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            );
          })()}
          <div>
            <label className="text-white/50 text-xs mb-1 block">Status</label>
            <Select value={form.status} onValueChange={(v) => setForm(p => ({ ...p, status: v }))}>
              <SelectTrigger className="input-dark w-full"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1F1F1F] border-[#333]">{STATUS_OPTIONS.filter(s => s !== "archived" && s !== "proposal_sent").map(s => <SelectItem key={s} value={s} className="text-white hover:bg-[#333] capitalize">{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button type="submit" className="btn-gold w-full" disabled={submitting} data-testid="modal-submit-booking">{submitting ? "Creating..." : "Create Booking"}</Button>
        </form>
      </div>
    </div>
  );
}
